import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import moment from "moment";

import * as codeActions from "../../../actions/codeActions";
import * as reportActions from "../../../actions/reportActions";
import * as exportActions from "../../../actions/exportActions";
import * as serviceActions from "../../../actions/serviceActions";
import { customToast as toast } from "../../../utils";

import SupportTicketDetailFilters from "./SupportTicketDetailFilters";
import SupportTicketDetailList from "./SupportTicketDetailList";

const SupportTicketDetailPage = ({ codes, actions, exporting, exported }) => {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    page: 0,
    pageSize: 50,
    filtered: {
      start_date: moment().startOf("year"),
      end_date: moment()
    }
  });
  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!codes.length) {
      actions.loadCodes();
    }
  }, [actions, codes]);

  useEffect(() => {
    if (exported) {
      const url = window.URL.createObjectURL(
        new Blob([exported], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `Support-Ticket-Detail-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      actions.clearExported();
    }
  }, [exported, actions]);

  const fetchData = async (
    pageSize = filters.pageSize,
    page = 0,
    filtered = filters.filtered
  ) => {
    setLoading(true);
    try {
      const report = await actions.getSupportTicketDetailReport({
        filtered,
        page,
        pageSize
      });
      if (report) {
        setData(report.tickets);
        setCount(report.count);
      }
    } catch (error) {
      toast.error(
        "Request too large. Please adjust filters or contact support."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChangeDate = (startDate, endDate) => {
    setFilters((prev) => ({
      ...prev,
      filtered: {
        ...prev.filtered,
        start_date: startDate || prev.filtered.start_date,
        end_date: endDate || prev.filtered.end_date
      }
    }));
  };

  const handleChange = (name) => (value) => {
    setLoading(false);
    if (Array.isArray(value)) {
      setFilters((prev) => ({
        ...prev,
        filtered: {
          ...prev.filtered,
          [name]: value.map((v) => ({ label: v.label, value: v.value }))
        }
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        filtered: { ...prev.filtered, [name]: value }
      }));
    }
  };

  const handlePageSizeChange = (pageSize) => {
    setFilters((prev) => ({ ...prev, pageSize }));
    fetchData(pageSize, filters.page);
  };

  const handlePageChange = async (page) => {
    setFilters((prev) => ({ ...prev, page }));
    fetchData(filters.pageSize, page);
  };

  const handleResetClick = () => {
    setFilters({
      page: 0,
      pageSize: 50,
      filtered: {
        start_date: moment().startOf("year"),
        end_date: moment()
      }
    });
    setData([]);
  };

  const handleExportPDF = () => {
    const queryFilters = {
      filtered: filters.filtered
    };
    const payload = {
      data: queryFilters,
      type: "SUPPORT_TICKET_DETAIL",
      format: "pdf"
    };
    actions.performExport(payload);
  };

  return (
    <div id="support-ticket-detail-page" className="report-page">
      <SupportTicketDetailFilters
        codes={codes}
        filters={filters}
        onChange={handleChange}
        onChangeDate={handleChangeDate}
        onFetchClick={fetchData}
        onResetClick={handleResetClick}
        loading={loading}
        disabled={loading || !Object.keys(filters).length}
        onPDFExport={handleExportPDF}
        exportDisabled={!data?.length || loading || exporting}
      />
      <SupportTicketDetailList
        data={data}
        loading={loading}
        count={count}
        onPageSizeChange={handlePageSizeChange}
        onPageChange={handlePageChange}
        page={filters.page}
        sorted={filters.sorted}
        pageSize={filters.pageSize}
        defaultPageSize={50}
      />
    </div>
  );
};

SupportTicketDetailPage.propTypes = {
  codes: PropTypes.array,
  actions: PropTypes.object,
  exporting: PropTypes.bool,
  exported: PropTypes.string
};

const mapStatesToProps = (state) => ({
  codes: state.reducers.codes,
  exporting: state.reducers.exporting,
  exported: state.reducers.exported
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      ...codeActions,
      ...reportActions,
      ...exportActions,
      ...serviceActions
    },
    dispatch
  )
});

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(SupportTicketDetailPage);
